import clsx from 'clsx';

export interface SendDealLinkButtonProps {
  conversationId: string | null;
  /**
   * Whether the active conversation has at least one version snapshot.
   * `conversation-model-versioning` Phase 6 (2026-05-18, ADR-0025):
   * the gate is ADDITIVE — a single `isDisabled` predicate composes both
   * conditions so the button never has two independent disable layers (Risk #47).
   */
  hasVersion: boolean;
  /**
   * Whether the active conversation's live state matches the current version
   * snapshot (i.e. no unsaved changes since the last version was created).
   * `builder-ux-overhaul` Phase 4 Session 4 (2026-05-22, ADR-0025):
   * AND-in-sync, additive to the existing predicate — the gate remains a single
   * `isDisabled` predicate composing all three conditions.
   */
  inSync: boolean;
  className?: string;
}

/**
 * `agent-ux-optimization` Phase 6 Session 8 (2026-05-08); Phase 6's disabled
 * placeholder removed by `external-sharing` Phase 3 Session 3.2 (2026-05-09).
 * `hasVersion` gate added by `conversation-model-versioning` Phase 6 (2026-05-18,
 * ADR-0025): requires ≥1 version snapshot before sharing is permitted.
 *
 * Send Deal Link CTA — top-right of the Builder Signal + Model headers.
 * Dispatches `arcanum:send-deal-link` window event with the active
 * conversationId; `<SendDealLinkModal>` (mounted at the Builder root) listens
 * for that event and opens with the conversation pre-bound.
 */
export function SendDealLinkButton({
  conversationId,
  hasVersion,
  inSync,
  className,
}: SendDealLinkButtonProps) {
  const isDisabled = conversationId === null || !hasVersion || !inSync;
  const tooltip =
    conversationId === null
      ? 'No active conversation to share.'
      : !hasVersion
      ? 'Create a version first.'
      : !inSync
      ? "Create a new version — your latest edits aren't in a shareable version yet."
      : 'Send a public deal link to a recipient outside Arcanum.';

  function handleClick() {
    if (isDisabled || conversationId === null) return;
    if (typeof window === 'undefined') return;
    try {
      window.dispatchEvent(
        new CustomEvent('arcanum:send-deal-link', {
          detail: { conversationId },
        })
      );
    } catch {
      // Defensive — CustomEvent unsupported envs shouldn't crash the page.
    }
  }

  return (
    <button
      type="button"
      data-testid="send-deal-link-button"
      data-disabled={isDisabled ? 'true' : 'false'}
      disabled={isDisabled}
      onClick={handleClick}
      title={tooltip}
      className={clsx(
        'flex items-center gap-1.5 px-2.5 py-1 border rounded-[2px] text-micro font-mono uppercase tracking-[0.5px] transition-colors',
        isDisabled
          ? 'border-panel-border bg-panel-bg text-text-dim cursor-not-allowed'
          : 'border-accent/30 bg-accent-dim text-accent hover:border-accent/50 cursor-pointer',
        className
      )}
    >
      <span aria-hidden="true">↗</span>
      <span>Send Deal Link</span>
    </button>
  );
}
